import { useState, useCallback, useMemo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import TimelineIcon from '@mui/icons-material/Timeline';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useActiveProject } from '../../context/ActiveProjectContext';
import { useProject } from '../../api/hooks/useProjects';
import { useArbeitspakete } from '../../api/hooks/useArbeitspakete';
import { useProjectDashboard } from '../../api/hooks/useProjectDashboard';
import { PROJECT_SIDEBAR_GROUPS, findGroupForPhase, flattenSidebarItems, PROJECT_DEFAULT_PHASE, type ProjectSidebarGroup, type ProjectSidebarItem } from '../../data/projectSidebarConfig';
import { PROJECT_PERMISSIONS } from '@pm/shared';
import { usePermissionContext } from '../../context/PermissionContext';
import { apLabel } from '../../pages/projects/lib/apLabel';
import { PROJECT_SIDEBAR_ICONS } from '../../data/projectSidebarIcons';

export const SIDEBAR_WIDTH = 260;
const APP_VERSION = `v${__APP_VERSION__}`;
const STORAGE_KEY_PHASES = 'pm_sidebar_open_phases';

interface DashboardSchritt { schritt: number; reifegrad?: number | null }

const GRAY = '#94a3b8';

function levelColor(level: number | null | undefined): string {
  if (level == null) return 'transparent';
  if (level >= 4) return '#16a34a'; // gruen — done
  if (level >= 2) return '#ca8a04'; // gelb — wip
  return '#dc2626';                  // rot — offen
}

function levelLabel(level: number | null | undefined, inactive: boolean): string {
  if (inactive) return 'Phase noch nicht erreicht';
  if (level == null) return '—';
  if (level >= 4) return `Reifegrad ${level} (Done)`;
  if (level >= 2) return `Reifegrad ${level} (In Arbeit)`;
  if (level >= 1) return `Reifegrad ${level} (Begonnen)`;
  return 'Reifegrad 0 (Nicht begonnen)';
}

function StatusDot({
  level,
  size = 8,
  inactive = false,
}: { level: number | null | undefined; size?: number; inactive?: boolean }) {
  if (!inactive && level == null) return <Box sx={{ width: size, height: size }} />;
  const color = inactive ? GRAY : levelColor(level);
  return (
    <Tooltip title={levelLabel(level, inactive)} placement="right" arrow>
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          bgcolor: color,
          flexShrink: 0,
        }}
      />
    </Tooltip>
  );
}

/**
 * Raw-Durchschnitt der Reifegrade einer Phase (0-5). Wird vom Phase-Gating
 * verwendet (Strenge Schwelle).
 */
function groupRawAvg(group: ProjectSidebarGroup, schritte: Map<number, number>): number | null {
  const stepNumbers = flattenSidebarItems(group.items).map((i) => i.step).filter((s): s is number => typeof s === 'number');
  if (stepNumbers.length === 0) return null;
  const values = stepNumbers.map((n) => schritte.get(n) ?? 0);
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/** True, sobald in der Phase mindestens ein Schritt einen Reifegrad > 0 hat. */
function groupTouched(group: ProjectSidebarGroup, schritte: Map<number, number>): boolean {
  return flattenSidebarItems(group.items)
    .map((i) => i.step)
    .filter((s): s is number => typeof s === 'number')
    .some((n) => (schritte.get(n) ?? 0) > 0);
}

/**
 * Anzeige-Level fuer den Phase-Header-Dot. Logik:
 *  - sum == 0  → 0 (rot)        — noch nichts begonnen
 *  - avg >= 4  → 5 (gruen)      — Phase im Wesentlichen abgeschlossen
 *  - sonst     → 3 (gelb)       — mindestens ein Schritt in Arbeit / fertig
 */
function groupDisplayLevel(group: ProjectSidebarGroup, schritte: Map<number, number>): number | null {
  const stepNumbers = flattenSidebarItems(group.items).map((i) => i.step).filter((s): s is number => typeof s === 'number');
  if (stepNumbers.length === 0) return null;
  const values = stepNumbers.map((n) => schritte.get(n) ?? 0);
  const sum = values.reduce((s, v) => s + v, 0);
  if (sum === 0) return 0;
  const avg = sum / values.length;
  if (avg >= 4) return 5;
  return 3;
}

/**
 * Phase-Gating: Eine Phase wird aktiv, sobald die Vorphase "in Arbeit" ist
 * (Raw-Durchschnitt >= 3) ODER in der Phase selbst bereits gearbeitet wurde
 * (irgendein Schritt mit Reifegrad > 0). Die Schritt-Reihenfolge ist damit
 * NICHT zwingend — wer direkt einen Planungs- oder Ausfuehrungs-Schritt
 * bearbeitet, aktiviert dessen Phase sofort.
 *
 * - overview, initiation: immer aktiv
 * - planning: aktiv wenn initiation-avg >= 3 ODER planning berührt
 * - execution: aktiv wenn planning aktiv & planning-avg >= 3 ODER execution berührt
 * - closure: aktiv wenn execution aktiv & execution-avg >= 3
 */
function isPhaseActive(phaseKey: string, schritte: Map<number, number>): boolean {
  if (phaseKey === 'overview' || phaseKey === 'initiation') return true;
  const groups = PROJECT_SIDEBAR_GROUPS;
  const findAvg = (key: string): number => {
    const g = groups.find((x) => x.key === key);
    return g ? (groupRawAvg(g, schritte) ?? 0) : 0;
  };
  const isTouched = (key: string): boolean => {
    const g = groups.find((x) => x.key === key);
    return g ? groupTouched(g, schritte) : false;
  };
  if (phaseKey === 'planning') return findAvg('initiation') >= 3 || isTouched('planning');
  if (phaseKey === 'execution') {
    return isTouched('execution') || (findAvg('planning') >= 3 && isPhaseActive('planning', schritte));
  }
  if (phaseKey === 'closure') return findAvg('execution') >= 3 && isPhaseActive('execution', schritte);
  return true;
}

function loadOpenPhases(fallback: string): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PHASES);
    if (stored !== null) return new Set(JSON.parse(stored) as string[]);
  } catch { /* ignore */ }
  return new Set([fallback]);
}

function saveOpenPhases(open: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY_PHASES, JSON.stringify([...open]));
  } catch { /* ignore */ }
}

export function Sidebar() {
  const { t } = useTranslation(['projects', 'common']);
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissionContext();
  // Verwaltungs-Link nur fuer Berechtigte (Schreibrouten verlangen admin.settings).
  const canManageChips = hasPermission('admin.settings');
  const { activeProjectId } = useActiveProject();
  const { data: projectResp } = useProject(activeProjectId ?? undefined);
  const project = (projectResp as unknown as { data?: { id: string; name: string } } | undefined)?.data ?? null;
  const { data: dashResp } = useProjectDashboard(activeProjectId ?? undefined);
  const { data: apResp } = useArbeitspakete(activeProjectId ?? undefined);
  const reifegradMap = useMemo(() => {
    const raw = (dashResp as unknown as { data?: { schritte?: DashboardSchritt[] } } | undefined)?.data?.schritte ?? [];
    const map = new Map<number, number>();
    raw.forEach((s) => map.set(s.schritt, s.reifegrad ?? 0));
    return map;
  }, [dashResp]);

  const isOnTimeline = location.pathname === '/' || location.pathname === '/timeline';
  const currentPhase = (() => {
    const m = location.pathname.match(/^\/projects\/[^/]+\/([^/?#]+)/);
    return m?.[1] ?? null;
  })();
  const currentAp = new URLSearchParams(location.search).get('ap');

  // Arbeitspakete dynamisch als Subpunkte unter „Projektplan" einhaengen, damit
  // man bei vielen Aufgaben direkt in ein einzelnes AP springen kann.
  const apChildren = useMemo<ProjectSidebarItem[]>(() => {
    const raw = (apResp as unknown as { data?: Array<{ id: string; nr: string; titel: string | null }> } | undefined)?.data ?? [];
    return raw.map((ap) => ({
      key: `projektplan:${ap.id}`,
      labelKey: '',
      label: `${apLabel(ap.nr)}  ${ap.titel ?? '—'}`,
      permission: PROJECT_PERMISSIONS.TASK.READ,
      apId: ap.id,
    }));
  }, [apResp]);

  const groups = useMemo<ProjectSidebarGroup[]>(() => {
    if (apChildren.length === 0) return PROJECT_SIDEBAR_GROUPS;
    return PROJECT_SIDEBAR_GROUPS.map((g) => ({
      ...g,
      items: g.items.map((it) => (it.key === 'projektplan'
        ? { ...it, children: [...(it.children ?? []), ...apChildren] }
        : it)),
    }));
  }, [apChildren]);

  const defaultOpenPhase = useMemo(() => {
    if (currentPhase) return findGroupForPhase(currentPhase).key;
    return findGroupForPhase(PROJECT_DEFAULT_PHASE).key;
  }, [currentPhase]);

  const [openPhases, setOpenPhases] = useState<Set<string>>(() => loadOpenPhases(defaultOpenPhase));

  const togglePhase = useCallback((key: string) => {
    setOpenPhases((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      saveOpenPhases(next);
      return next;
    });
  }, []);

  // Aufklapp-Status der Eltern-Items mit Unterpunkten — per Default offen.
  const [openItems, setOpenItems] = useState<Set<string>>(() => {
    const s = new Set<string>();
    PROJECT_SIDEBAR_GROUPS.forEach((g) => g.items.forEach((it) => { if (it.children?.length) s.add(it.key); }));
    // Projektplan-AP-Subpunkte aufklappen, wenn man sich im Projektplan befindet.
    if (currentPhase === 'projektplan') s.add('projektplan');
    return s;
  });

  const toggleItem = useCallback((key: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // Rendert einen Navigations-Eintrag (Hauptpunkt depth=0, Unterpunkt depth=1).
  // Eltern mit children sind klickbar (eigene Seite) UND ueber das Chevron
  // auf-/zuklappbar.
  const renderNavItem = (item: ProjectSidebarItem, phaseActive: boolean, depth: number): ReactNode => {
    const isApItem = !!item.apId;
    const selected = isApItem
      ? (currentPhase === 'projektplan' && currentAp === item.apId)
      : (item.key === currentPhase && !(item.key === 'projektplan' && currentAp));
    const hasChildren = !!item.children?.length;
    const childActive = item.children?.some((c) => c.key === currentPhase || (c.apId != null && c.apId === currentAp)) ?? false;
    const itemLevel = phaseActive && item.step != null ? (reifegradMap.get(item.step) ?? 0) : null;
    const Icon = PROJECT_SIDEBAR_ICONS[item.key];
    const itemOpen = openItems.has(item.key);
    return (
      <Box key={item.key}>
        <ListItemButton
          selected={selected}
          onClick={() => navigate(isApItem
            ? `/projects/${activeProjectId}/projektplan?ap=${item.apId}`
            : `/projects/${activeProjectId}/${item.key}`)}
          sx={{
            py: 0.5, mx: 1, pl: depth === 0 ? 2 : 4, pr: 1, borderRadius: 1,
            display: 'flex', alignItems: 'center', gap: 1,
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
            },
          }}
        >
          <StatusDot level={itemLevel} size={8} inactive={!phaseActive && item.step != null} />
          {Icon && (
            <Icon sx={{ fontSize: 18, color: selected ? 'inherit' : 'text.secondary', flexShrink: 0 }} />
          )}
          <ListItemText
            primary={item.label ?? t(item.labelKey, item.key)}
            primaryTypographyProps={{ variant: 'body2', fontWeight: selected || childActive ? 600 : 400 }}
          />
          {hasChildren && (
            <Box
              component="span"
              onClick={(e) => { e.stopPropagation(); toggleItem(item.key); }}
              sx={{ display: 'flex', alignItems: 'center', ml: 0.5, color: selected ? 'inherit' : 'text.secondary' }}
            >
              {itemOpen
                ? <ExpandLessIcon sx={{ fontSize: 18 }} />
                : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
            </Box>
          )}
        </ListItemButton>
        {hasChildren && (
          <Collapse in={itemOpen} timeout="auto" unmountOnExit>
            <List dense disablePadding>
              {item.children!.map((child) => renderNavItem(child, phaseActive, depth + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          overflowX: 'hidden',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Brand */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 32, height: 32, borderRadius: 1, bgcolor: 'primary.main',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <Typography variant="caption" fontWeight={800} sx={{ color: '#fff', fontSize: '0.6rem', letterSpacing: '-0.5px' }}>PHQ</Typography>
          </Box>
          <Typography variant="subtitle1" fontWeight={700} noWrap>Project HQ</Typography>
        </Box>
        <Divider />

        {/* Primary nav: Projektliste */}
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <List dense disablePadding sx={{ pt: 1 }}>
            <ListItemButton
              selected={isOnTimeline}
              onClick={() => navigate('/timeline')}
              sx={{ px: 2, py: 0.75, mx: 1, borderRadius: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <TimelineIcon fontSize="small" color={isOnTimeline ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText
                primary={t('projects:timeline.navLabel', 'Programm HQ')}
                primaryTypographyProps={{ variant: 'body2', fontWeight: isOnTimeline ? 600 : 500 }}
              />
            </ListItemButton>
            {/* Menüpunkt „Projekte" entfällt — die Projektliste liegt jetzt auf
                der Programm-HQ-Seite unter der Timeline. Die Prozesslandkarte
                liegt in der Projekt-Übersicht (projektbezogen). */}
          </List>

          {/* Aktives Projekt: Phasen-Akkordeon */}
          {activeProjectId && project && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ px: 2, pt: 0.5, pb: 1 }}>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                  {t('projects:sidebar.activeProject', 'Aktives Projekt')}
                </Typography>
                <Typography variant="body2" fontWeight={600} noWrap title={project.name}>
                  {project.name}
                </Typography>
              </Box>

              {groups.map((group) => {
                const isOpen = openPhases.has(group.key);
                const groupHasActive = flattenSidebarItems(group.items).some((i) => i.key === currentPhase);
                const phaseActive = isPhaseActive(group.key, reifegradMap);
                const aggLevel = phaseActive ? groupDisplayLevel(group, reifegradMap) : null;
                return (
                  <Box key={group.key}>
                    <Box
                      onClick={() => togglePhase(group.key)}
                      sx={{
                        px: 2, py: 0.75, mx: 1, mt: 0.5, borderRadius: 1, display: 'flex',
                        alignItems: 'center', justifyContent: 'space-between',
                        cursor: 'pointer', userSelect: 'none',
                        // Phasen-Hauptpunkte als Hintergrund-Band: hebt sie in ALLEN
                        // Themes klar von den Unterpunkten ab. Mehrere unabhaengige
                        // Signale (Fuellung + Rahmen + groessere/gespreizte Schrift),
                        // da der Fuellkontrast in dunklen Themes allein zu schwach waere.
                        bgcolor: 'var(--pm-bg-muted)',
                        border: '1px solid',
                        borderColor: groupHasActive ? 'primary.main' : 'divider',
                        transition: 'filter 120ms ease',
                        '&:hover': { filter: 'brightness(1.08)' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <StatusDot level={aggLevel} size={10} inactive={!phaseActive} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            fontSize: '1rem',
                            letterSpacing: 0.4,
                            color: groupHasActive ? 'primary.main' : 'text.primary',
                          }}
                        >
                          {t(group.labelKey, group.key)}
                        </Typography>
                      </Box>
                      {isOpen
                        ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      }
                    </Box>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <List dense disablePadding>
                        {group.items.map((item) => renderNavItem(item, phaseActive, 0))}
                      </List>
                    </Collapse>
                  </Box>
                );
              })}
            </>
          )}

          {/* Globale Links — Werkzeuge / Help / Einstellungen (unter Trennlinie) */}
          <Divider sx={{ my: 1.5 }} />
          {[
            { key: 'benutzer', label: t('sidebar.global.users', 'Benutzerverwaltung'), Icon: GroupOutlinedIcon, path: '/benutzerverwaltung' },
            ...(canManageChips
              ? [{ key: 'topbarChips', label: t('common:topbarChips.navLabel', 'TopBar-Chips'), Icon: LabelOutlinedIcon, path: '/topbar-chips' }]
              : []),
            { key: 'werkzeuge', label: t('sidebar.global.werkzeuge', 'Werkzeuge'), Icon: BuildOutlinedIcon, path: '/werkzeuge' },
            { key: 'help', label: t('sidebar.global.help', 'Help Center'), Icon: HelpOutlineOutlinedIcon, path: '/help' },
            { key: 'settings', label: t('sidebar.global.settings', 'Einstellungen'), Icon: SettingsOutlinedIcon, path: '/settings' },
          ].map(({ key, label, Icon, path }) => {
            const selected = location.pathname === path;
            return (
              <ListItemButton
                key={key}
                selected={selected}
                onClick={() => navigate(path)}
                sx={{
                  py: 0.75, mx: 1, pl: 2, pr: 1.5, borderRadius: 1,
                  display: 'flex', alignItems: 'center', gap: 1,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' },
                  },
                }}
              >
                <Icon sx={{ fontSize: 18, color: selected ? 'inherit' : 'text.secondary', flexShrink: 0 }} />
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: selected ? 600 : 500 }}
                />
              </ListItemButton>
            );
          })}
        </Box>

        {/* Footer */}
        <Divider />
        <Box sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
            {APP_VERSION}
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
}
